import {Alert, Button, Code, Container, Group, NumberInput, Stack, Stepper, Text, Title} from '@mantine/core';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {CustomerSelector} from '../components/CustomerSelector';
import {SubscriptionSelector} from '../components/SubscriptionSelector';
import {PriceSelector} from '../components/PriceSelector';
import {FlowResultDisplay} from '../components/FlowResultDisplay';
import {FlowResult, extractErrorMessage} from '../types';
import {fetch as fetchSubscription} from '../../subscriptions/services/SubscriptionsService';
import {update as updateSubscriptionItem} from '../../subscription-items/services/SubscriptionItemsService';
import {Subscription} from '../../subscriptions/types';
import {SubscriptionItem} from '../../subscription-items/types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';
import {AiOutlineInfoCircle} from 'react-icons/ai';

export const UpdateSubscriptionPage = () => {
    const navigate = useNavigate();
    const [active, setActive] = useState(0);
    const [customerId, setCustomerId] = useState<string | null>(null);
    const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
    const [newPriceId, setNewPriceId] = useState<string | null>(null);
    const [quantity, setQuantity] = useState<number>(1);
    const [processing, setProcessing] = useState(false);
    const [result, setResult] = useState<FlowResult | null>(null);

    const subscriptionQuery = useQuery({
        queryKey: ['subscription-detail', subscriptionId],
        queryFn: () => fetchSubscription(subscriptionId!),
        enabled: !!subscriptionId,
    });

    const currentItems = (subscriptionQuery.data as Subscription)?.items || [];

    const handleUpdate = async () => {
        if (!subscriptionId || !newPriceId || currentItems.length === 0) return;
        setProcessing(true);
        try {
            const itemToUpdate = currentItems[0];
            const updated = await updateSubscriptionItem({
                id: itemToUpdate.id,
                price: newPriceId,
                quantity,
                prorationBehavior: 'create_prorations',
            } as unknown as SubscriptionItem);

            console.log('Subscription item updated:', updated);
            notifySuccess('Subscription updated');
            setResult({
                success: true,
                subscriptionId,
                status: 'updated',
            });
            setActive(1);
        } catch (e) {
            notifyError(e);
            setResult({success: false, error: extractErrorMessage(e)});
            setActive(1);
        } finally {
            setProcessing(false);
        }
    };

    const handleReset = () => {
        setActive(0);
        setCustomerId(null);
        setSubscriptionId(null);
        setNewPriceId(null);
        setQuantity(1);
        setResult(null);
    };

    return (
        <Container size="sm" py="xl">
            <Stack gap="xl">
                <div>
                    <Title order={3}>Update Subscription (Plan Change)</Title>
                    <Text c="dimmed" size="sm">
                        Change the price/plan of an active subscription. Prorations will be created automatically.
                    </Text>
                </div>

                <Stepper active={active} allowNextStepsSelect={false}>
                    <Stepper.Step label="Select Details"/>
                    <Stepper.Step label="Result"/>
                </Stepper>

                {active === 0 && (
                    <Stack gap="md">
                        <CustomerSelector value={customerId} onChange={(v) => { setCustomerId(v); setSubscriptionId(null); }}/>
                        <SubscriptionSelector value={subscriptionId} onChange={setSubscriptionId} customerId={customerId} activeOnly/>

                        {currentItems.length > 0 && (
                            <Alert icon={<AiOutlineInfoCircle/>} color="gray" title="Current Plan">
                                {currentItems.map(item => (
                                    <Text key={item.id} size="sm">
                                        Item: <Code>{item.id}</Code> — Price: <Code>{item.price}</Code> — Qty: {item.quantity}
                                    </Text>
                                ))}
                            </Alert>
                        )}

                        <PriceSelector value={newPriceId} onChange={setNewPriceId} recurringOnly/>
                        <NumberInput
                            label="Quantity"
                            value={quantity}
                            onChange={v => setQuantity(Number(v) || 1)}
                            min={1}
                        />
                        <Group justify="space-between">
                            <Button variant="subtle" onClick={() => navigate('/payment-flow-tester')}>Back</Button>
                            <Button
                                onClick={handleUpdate}
                                disabled={!subscriptionId || !newPriceId || currentItems.length === 0}
                                loading={processing}
                            >
                                Update Subscription
                            </Button>
                        </Group>
                    </Stack>
                )}

                {active === 1 && result && (
                    <FlowResultDisplay result={result} onReset={handleReset}/>
                )}
            </Stack>
        </Container>
    );
};
